import { useEffect, useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Mail, Phone as PhoneIcon, MapPin, Home as HomeIcon, Calendar } from "lucide-react";

export default function LeadsAdmin({ password }) {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const r = await fetch(`${API}/admin/leads`, {
                    headers: { "X-Admin-Password": password },
                });
                if (!r.ok) throw new Error();
                const d = await r.json();
                if (active) setLeads(d);
            } catch {
                toast.error("Couldn't load leads.");
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [password]);

    const sources = leads.reduce((acc, l) => {
        acc[l.source] = (acc[l.source] || 0) + 1;
        return acc;
    }, {});

    return (
        <div data-testid="leads-admin">
            <div className="text-ink-soft text-sm mb-8">
                {leads.length} total
                {Object.keys(sources).length > 0 && " · "}
                {Object.entries(sources)
                    .map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`)
                    .join(" · ")}
            </div>
            {loading ? (
                <div className="text-center text-ink-soft py-20">Loading...</div>
            ) : leads.length === 0 ? (
                <div className="text-center text-ink-soft py-20 border border-dashed border-line-soft">
                    No leads yet.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {leads.map((l) => (
                        <article
                            key={l.id}
                            className="bg-bone border border-line-soft p-6 hover:border-gold/40 transition-colors"
                            data-testid={`admin-lead-${l.id}`}
                        >
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div>
                                    <div className="font-serif text-xl text-ink leading-tight">
                                        {l.name}
                                    </div>
                                    <div className="text-[11px] uppercase tracking-[0.18em] text-gold mt-1">
                                        {l.source.replace(/_/g, " ")}
                                    </div>
                                </div>
                                <div className="text-[11px] text-ink-soft flex items-center gap-1">
                                    <Calendar size={11} />
                                    {new Date(l.created_at).toLocaleDateString("en-AU")}
                                </div>
                            </div>
                            <div className="hairline opacity-50 my-3" />
                            <dl className="text-sm space-y-2">
                                <Row icon={Mail}>
                                    <a href={`mailto:${l.email}`} className="text-ink hover:text-gold break-all">
                                        {l.email}
                                    </a>
                                </Row>
                                {l.phone && (
                                    <Row icon={PhoneIcon}>
                                        <a href={`tel:${l.phone}`} className="text-ink hover:text-gold">
                                            {l.phone}
                                        </a>
                                    </Row>
                                )}
                                {(l.suburb || l.property_type) && (
                                    <Row icon={MapPin}>
                                        <span className="text-ink">
                                            {[
                                                l.suburb,
                                                l.property_type,
                                                l.bedrooms ? `${l.bedrooms} bed` : null,
                                            ]
                                                .filter(Boolean)
                                                .join(" · ")}
                                        </span>
                                    </Row>
                                )}
                                {l.estimated_monthly && (
                                    <Row icon={HomeIcon}>
                                        <span className="text-ink">
                                            Est. ${Number(l.estimated_monthly).toLocaleString()} / month
                                        </span>
                                    </Row>
                                )}
                                {l.message && (
                                    <div className="pt-3">
                                        <div className="eyebrow text-ink-soft mb-1">Message</div>
                                        <p className="text-sm text-ink font-light leading-relaxed whitespace-pre-line">
                                            {l.message}
                                        </p>
                                    </div>
                                )}
                            </dl>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}

function Row({ icon: Icon, children }) {
    return (
        <div className="flex items-start gap-2 text-sm">
            <Icon size={13} strokeWidth={1.3} className="text-gold mt-1 shrink-0" />
            <div className="flex-1">{children}</div>
        </div>
    );
}
